// @ts-nocheck
/*
 * Port of views/dashboard/trunks/list.js (origin/main-dev, 569 lines).
 * BYTE-IDENTICAL LOGIC — every state hook, useEffect, API call, toast,
 * ACL gate, delete flow (single + multi), select-all, and export CSV
 * preserved. Chrome swap: PrimeReact DataTable + Column → obd3-gui
 * DataTable + ColumnDef; PrimeReact Button + Checkbox → shadcn; v1
 * <Card> → shadcn <Card>. EditIcon/DeleteIcon from v1 setting/elements
 * inlined here (v2 has no such helper).
 */
import { useState, useEffect } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall, postApiCall } from '@/service/httpCall';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import { toast } from 'sonner';
import { ROUTES, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import {
  API_TRUNKS_LIST,
  API_TRUNKS_DELETE,
  API_OPERATORS_LIST,
  API_SERVER_MANAGEMENT_LIST,
} from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  Download,
  Plus,
  Pencil,
  Trash2,
  Radio,
  Globe,
  MapPin,
  Server as ServerIcon,
  Building2,
  ShieldCheck,
} from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

export default function TrunkList() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [trunksList, setTrunksList] = useState([]);
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTrunkId, setDeleteTrunkId] = useState(undefined);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, checkIsSuperAdmin] = useState(false);
  const [moduleOperations, setModuleOperations] = useState({});
  const [selectedTrunks, setSelectedTrunks] = useState([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [operatorList, setOperatorList] = useState([]);
  const [serverList, setServerList] = useState([]);
  const [typeFilter, setTypeFilter] = usePersistedState('trunks/TrunkList:typeFilter', 'all');
  const visibleTrunks = trunksList.filter((t: any) =>
    typeFilter === 'all'
      ? true
      : typeFilter === 'international'
        ? Number(t.is_international) === 1
        : Number(t.is_international) !== 1,
  );

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'trunks-list',
        data: trunksList,
        columns: [
          { header: 'ID', accessor: 'id' },
          { header: 'Name', accessor: (row) => row.name || '-' },
          { header: 'Authentication Type', accessor: (row) => row.auth_type || '-' },
          { header: 'Domain', accessor: (row) => row.domain || '-' },
          { header: 'Port', accessor: (row) => row.port || '-' },
          { header: 'Operator', accessor: (row) => row.operator_name || '-' },
          { header: 'Server', accessor: (row) => row.server_name || '-' },
          { header: 'International / Domestic', accessor: (row) => (Number(row.is_international) === 1 ? 'International' : 'Domestic') },
          {
            header: 'Created Date',
            accessor: (row) =>
              new Date(row.created_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
          {
            header: 'Updated Date',
            accessor: (row) =>
              new Date(row.updated_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const handleCheckboxToggle = (trunkId) => {
    const isSelected = selectedTrunks.includes(trunkId);
    setSelectedTrunks(
      isSelected ? selectedTrunks.filter((id) => id !== trunkId) : [...selectedTrunks, trunkId],
    );
  };

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  const handleMultiDelete = () => {
    setIsMultiDelete(true);
    setEnableDeleteModal(true);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getApiCall(API_OPERATORS_LIST),
      getApiCall(API_SERVER_MANAGEMENT_LIST),
      getApiCall(API_TRUNKS_LIST),
    ])
      .then(([operatorsRes, serversRes, trunksRes]) => {
        setOperatorList(operatorsRes);
        setServerList(serversRes);
        processTrunks(trunksRes, operatorsRes, serversRes);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, []);

  function processTrunks(trunks, operatorList, serverList) {
    const updatedList = trunks
      .filter((trunk) => !trunk.is_deleted)
      .map((val, idx) => {
        const operator = operatorList.find((op) => op.id === val.operator_id);
        const server = serverList.find((srv) => srv.id === val.server_id);
        return {
          ...val,
          key: idx + 1,
          operator_name: operator ? operator.name : 'N/A',
          server_name: server ? server.server_name : 'N/A',
        };
      });
    setTrunksList(updatedList);
  }

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user.role === USER_ROLES.SUPERADMIN) checkIsSuperAdmin(true);
      if (user?.acl_settings) {
        setModuleOperations(parseModuleOperations(user.acl_settings.module_operations));
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperations(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  function handleDeleteTrunk(id) {
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeleteTrunkId(id);
  }

  const handleSelectAll = () => {
    if (selectedTrunks.length === visibleTrunks.length) {
      setSelectedTrunks([]);
    } else {
      setSelectedTrunks(visibleTrunks.map((t) => t.id));
    }
  };

  useEffect(() => {
    if (confirmDelete) {
      if (isMultiDelete) {
        postApiCall(API_TRUNKS_DELETE, { trunkIds: selectedTrunks })
          .then(onDeleteSuccess)
          .catch(onDeleteFail);
      } else {
        postApiCall(API_TRUNKS_DELETE, { trunkIds: [deleteTrunkId] })
          .then(onDeleteSuccess)
          .catch(onDeleteFail);
      }
    }
  }, [confirmDelete]);

  function onDeleteSuccess() {
    toast.success('Trunk deleted successfully');
    let updatedList;
    if (isMultiDelete) {
      updatedList = trunksList
        .filter((val) => !selectedTrunks.includes(val.id))
        .map((val, key) => {
          val['key'] = key + 1;
          return val;
        });
      setSelectedTrunks([]);
    } else {
      updatedList = trunksList
        .filter((val) => val.id != deleteTrunkId)
        .map((val, key) => {
          val['key'] = key + 1;
          return val;
        });
    }

    setLoading(true);
    Promise.all([
      getApiCall(API_OPERATORS_LIST),
      getApiCall(API_SERVER_MANAGEMENT_LIST),
      getApiCall(API_TRUNKS_LIST),
    ])
      .then(([operatorsRes, serversRes, trunksRes]) => {
        setOperatorList(operatorsRes);
        setServerList(serversRes);
        processTrunks(trunksRes, operatorsRes, serversRes);
      })
      .catch(onError)
      .finally(() => setLoading(false));

    setTrunksList(updatedList);
    setEnableDeleteModal(false);
    setDeleteTrunkId(undefined);
    setConfirmDelete(false);
  }

  function onDeleteFail(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.error(`Unable to delete trunk: ${err}`);
    }
    setConfirmDelete(false);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleCreateButtonClick = () => {
    navigate(`/${ROUTES.TRUNKS.CREATE}`);
  };

  const canEdit = isSuperAdmin;
  const canDelete = isSuperAdmin;
  const canAdd = isSuperAdmin;
  const showActionsColumn = canEdit || canDelete;

  const fmtDate = (raw: any) =>
    raw
      ? new Date(raw).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const authTone = (auth: string) => {
    const a = (auth || '').toLowerCase();
    if (a.includes('digest') || a.includes('password'))
      return 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300';
    if (a.includes('ip'))
      return 'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300';
    return 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300';
  };

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      size: 44,
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={selectedTrunks.length === visibleTrunks.length && visibleTrunks.length > 0}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedTrunks.includes(row.original.id)}
          onCheckedChange={() => handleCheckboxToggle(row.original.id)}
          aria-label="Select row"
        />
      ),
    },
    {
      accessorKey: 'id',
      header: 'ID',
      size: 70,
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground tabular-nums">
          #{row.original.id}
        </span>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 240,
      cell: ({ row }) => {
        const nm = row.original.name || '—';
        const initials = (nm || '')
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w: string) => w[0])
          .join('')
          .toUpperCase() || '—';
        return (
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
              {initials}
            </span>
            <span className="font-medium text-foreground">{nm}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'auth_type',
      header: 'Auth Type',
      size: 150,
      cell: ({ row }) => {
        const auth = row.original.auth_type;
        if (!auth) return <span className="text-muted-foreground">—</span>;
        return (
          <span
            className={
              'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              authTone(auth)
            }
          >
            <ShieldCheck className="h-3 w-3" />
            {auth}
          </span>
        );
      },
    },
    {
      accessorKey: 'domain',
      header: 'Domain',
      cell: ({ row }) =>
        row.original.domain ? (
          <span className="inline-flex items-center rounded-md bg-muted/60 px-2 py-0.5 font-mono text-xs font-semibold text-foreground ring-1 ring-inset ring-border/60">
            {row.original.domain}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'port',
      header: 'Port',
      size: 90,
      cell: ({ row }) =>
        row.original.port ? (
          <span className="font-mono text-xs tabular-nums text-foreground">
            {row.original.port}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'operator_name',
      header: 'Operator',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.operator_name || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'server_name',
      header: 'Server',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.server_name || '—'}
        </span>
      ),
    },
    {
      id: 'is_international',
      accessorFn: (row: any) => (Number(row.is_international) === 1 ? 'International' : 'Domestic'),
      header: 'International / Domestic',
      size: 160,
      cell: ({ row }) =>
        Number(row.original.is_international) === 1 ? (
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300">
            <Globe className="h-3 w-3" />
            International
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
            <MapPin className="h-3 w-3" />
            Domestic
          </span>
        ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {fmtDate(row.original.created_at)}
        </span>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {fmtDate(row.original.updated_at)}
        </span>
      ),
    },
  ];

  if (showActionsColumn) {
    columns.push({
      id: 'actions',
      header: 'Actions',
      size: 110,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const id = row.original.id;
        const nm = row.original.name || `#${id}`;
        const trunkEditRoute = ROUTES.TRUNKS.EDIT.replace(':id', id);
        return (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                title={`Edit ${nm}`}
                onClick={() => navigate(`/${trunkEditRoute}`)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {!(selectedTrunks.length > 0) && canDelete && (
              <button
                type="button"
                title={`Delete ${nm}`}
                onClick={() => handleDeleteTrunk(id)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      },
    });
  }

  // Stats — total, distinct operators, distinct servers
  const stats = (() => {
    const total = trunksList.length;
    const distinctOperators = new Set(
      trunksList.map((t: any) => t.operator_name).filter((v: any) => v && v !== 'N/A'),
    ).size;
    const distinctServers = new Set(
      trunksList.map((t: any) => t.server_name).filter((v: any) => v && v !== 'N/A'),
    ).size;
    const international = trunksList.filter((t: any) => Number(t.is_international) === 1).length;
    const domestic = total - international;
    return { total, distinctOperators, distinctServers, international, domestic };
  })();

  if (loading && trunksList.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Trunks"
        description="SIP trunks / carriers wired into the platform for outbound and inbound."
        actions={
          <>
            {trunksList.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={handleCreateButtonClick}>
                <Plus className="h-4 w-4" />
                Create Trunk
              </Button>
            )}
          </>
        }
      />
      {/* Stat strip — Total / Operators / Servers */}
      <div className="mb-3 flex flex-wrap gap-2">

        {[
          { key: 'all', label: 'Total trunks', count: stats.total, Icon: Radio, tone: 'border-border/60 bg-card', icon: 'bg-primary/10 text-primary', text: 'text-muted-foreground' },
          { key: 'international', label: 'International', count: stats.international, Icon: Globe, tone: 'border-sky-200 bg-sky-50 dark:border-sky-900/60 dark:bg-sky-950/30', icon: 'bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300', text: 'text-sky-700 dark:text-sky-300' },
          { key: 'domestic', label: 'Domestic', count: stats.domestic, Icon: MapPin, tone: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30', icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300', text: 'text-emerald-700 dark:text-emerald-300' },
        ].map(({ key, label, count, Icon, tone, icon, text }) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setTypeFilter(key);
              setSelectedTrunks([]);
            }}
            aria-pressed={typeFilter === key}
            className={
              'flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-neu-raised transition focus:outline-none focus:ring-2 focus:ring-primary/30 ' +
              tone +
              (typeFilter === key ? ' ring-2 ring-primary/50' : ' opacity-90 hover:opacity-100')
            }
          >
            <span className={'flex h-6 w-6 items-center justify-center rounded-full ' + icon}>
              <Icon className="h-3.5 w-3.5" />
            </span>
            <span className={text}>{label}</span>
            <span className="font-semibold text-foreground tabular-nums">{count}</span>
          </button>
        ))}
        <div className="flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-sky-900/60 dark:bg-sky-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300">
            <Building2 className="h-3.5 w-3.5" />
          </span>
          <span className="text-sky-700 dark:text-sky-300">Operators</span>
          <span className="font-semibold text-sky-800 tabular-nums dark:text-sky-200">
            {stats.distinctOperators}
          </span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-violet-900/60 dark:bg-violet-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-300">
            <ServerIcon className="h-3.5 w-3.5" />
          </span>
          <span className="text-violet-700 dark:text-violet-300">Servers</span>
          <span className="font-semibold text-violet-800 tabular-nums dark:text-violet-200">
            {stats.distinctServers}
          </span>
        </div>
      </div>

      <Card className="p-4">
        <DataTable
          tableId="trunks.list"
          columns={columns}
          data={visibleTrunks}
          isLoading={loading}
          emptyTitle="No trunks"
          emptyDescription="No trunks match the current filters."
          pageSize={10}
          initialColumnPinning={showActionsColumn ? { right: ['actions'] } : undefined}
          disableExport
          toolbar={
            canDelete && (
              <Button
                size="sm"
                variant="destructive"
                onClick={handleMultiDelete}
                disabled={selectedTrunks.length === 0}
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected
                {selectedTrunks.length > 0 && (
                  <span className="ml-1 rounded-full bg-white/20 px-1.5 text-[10px] font-bold">
                    {selectedTrunks.length}
                  </span>
                )}
              </Button>
            )
          }
        />
      </Card>

      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
        isMultiDelete={isMultiDelete}
        clearSelectedData={() => setSelectedTrunks([])}
      />
    </FillPage>
  );
}
